import { useRef, useCallback, useState, type RefObject } from 'react'

export interface UseSwipeDownOptions {
  threshold?: number
  dragHandleRef?: RefObject<HTMLElement>
}

export interface SwipeDownHandlers {
  onTouchStart: (e: React.TouchEvent) => void
  onTouchMove: (e: React.TouchEvent) => void
  onTouchEnd: () => void
}

export interface UseSwipeDownReturn {
  swipeHandlers: SwipeDownHandlers
  swipeOffset: number
  isDragging: boolean
}

/**
 * Hook for implementing swipe-down-to-dismiss gesture on modals and drawers.
 * Extracted from TaskList.tsx swipe pattern, adapted for vertical axis.
 *
 * @param onDismiss - Callback fired when swipe exceeds threshold
 * @param options - Configuration options
 * @param options.threshold - Distance in pixels required to trigger dismiss (default: 80)
 * @param options.dragHandleRef - Optional ref to restrict gesture to specific element (prevents conflicts with scrollable content)
 *
 * @returns Object with swipeHandlers to attach to target element, current swipeOffset for visual feedback, and isDragging state
 *
 * @example
 * ```tsx
 * const headerRef = useRef<HTMLDivElement>(null)
 * const { swipeHandlers, swipeOffset, isDragging } = useSwipeDown(onClose, {
 *   dragHandleRef: headerRef
 * })
 *
 * return (
 *   <div
 *     ref={headerRef}
 *     {...swipeHandlers}
 *     style={{ transform: `translateY(${Math.max(0, swipeOffset)}px)` }}
 *   >
 *     {content}
 *   </div>
 * )
 * ```
 */
export function useSwipeDown(
  onDismiss: () => void,
  options: UseSwipeDownOptions = {}
): UseSwipeDownReturn {
  const { threshold = 80, dragHandleRef } = options

  const [swipeOffset, setSwipeOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const swipeStartX = useRef<number>(0)
  const swipeStartY = useRef<number>(0)
  const swipeAxis = useRef<'x' | 'y' | null>(null)

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      // If dragHandleRef is provided, only allow swipe from that element
      if (dragHandleRef?.current) {
        const target = e.target as HTMLElement
        if (!dragHandleRef.current.contains(target)) {
          return
        }
      }

      swipeStartX.current = e.touches[0].clientX
      swipeStartY.current = e.touches[0].clientY
      swipeAxis.current = null
      setIsDragging(true)
      setSwipeOffset(0)
    },
    [dragHandleRef]
  )

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging) return

    const dx = e.touches[0].clientX - swipeStartX.current
    const dy = e.touches[0].clientY - swipeStartY.current

    // Lock axis on first significant movement (>5px)
    if (!swipeAxis.current) {
      if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 5) {
        swipeAxis.current = 'y'
      } else if (Math.abs(dx) > 5) {
        swipeAxis.current = 'x'
      }
    }

    // Only handle vertical swipes
    if (swipeAxis.current === 'y') {
      e.preventDefault()
      // Only allow downward swipes (dy > 0), clamp upper limit
      const clampedOffset = Math.max(0, Math.min(dy, threshold + 40))
      setSwipeOffset(clampedOffset)
    }
  }, [isDragging, threshold])

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return

    if (swipeAxis.current === 'y' && swipeOffset >= threshold) {
      // Trigger dismiss if threshold exceeded
      onDismiss()
    }

    // Reset state
    setIsDragging(false)
    setSwipeOffset(0)
    swipeAxis.current = null
  }, [isDragging, swipeOffset, threshold, onDismiss])

  return {
    swipeHandlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
    swipeOffset,
    isDragging,
  }
}
